import { useState, useEffect, useRef, lazy, Suspense, ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";

const GlobalMap = lazy(() => import("@/components/GlobalMap"));
import { RESPONSIVE, SPACING, TABS } from "@/design-system";
import { cn } from "@/lib/utils";

interface MapSectionProps {
  title: ReactNode;
  subtitle?: ReactNode;
  helpText?: ReactNode;
  className?: string;
  locationCode?: string;
  locationCoordinates?: string;
}

export const MapSection = ({
  title,
  subtitle,
  helpText,
  className = "bg-muted/30",
  locationCode,
  locationCoordinates
}: MapSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef} 
      data-nosnippet 
      className={`${className} py-16`}
    >
      <div className="container">
        <h2 className={cn(RESPONSIVE.titleSection, SPACING.marginBottom4)}>
          {title}
        </h2>
        {subtitle && (
          <p className={cn('mb-6 text-lg text-muted-foreground text-center')}>
            {subtitle}
          </p>
        )}
        {isVisible ? (
          <Suspense fallback={<Skeleton className="min-h-[400px] h-[600px] max-h-[700px] w-full rounded-lg" />}>
            <GlobalMap 
              initialLocationCode={locationCode}
              initialLocationCoordinates={locationCoordinates}
            />
          </Suspense>
        ) : (
          <Skeleton className="min-h-[400px] h-[600px] max-h-[700px] w-full rounded-lg" />
        )}
        {helpText && (
          <p className={cn(TABS.contentNested, 'text-center text-muted-foreground')}>
            {helpText}
          </p>
        )}
      </div>
    </section>
  );
};
